import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab
} from '@mui/material';
import { Add, Dashboard, Download } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useAuth } from '../contexts/AuthContext';
import { roleMatches } from '../utils/rolePermissions';
import { hasGrantModuleAccess, hasGrantTabAccess } from '../utils/viewPermissions';
import { ROLES } from '../constants/roles';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../contexts/ToastContext';

// Workflow dialog components
import CAMOQMAcknowledgment from './maintenance/CAMOQMAcknowledgment';
import PlanningActions from './maintenance/PlanningActions';
import StoreActions from './maintenance/StoreActions';
import ExternalAgencyActions from './maintenance/ExternalAgencyActions';
import MROActions from './maintenance/MROActions';
import MROEngineerCompletion from './maintenance/MROEngineerCompletion';
import MROHandoverDialog from './maintenance/MROHandoverDialog';
import MROAssignEngineersDialog from './maintenance/MROAssignEngineersDialog';
import MroQmCompletionAcknowledgment from './maintenance/MroQmCompletionAcknowledgment';
import CAMOQMCompletionAcknowledgment from './maintenance/CAMOQMCompletionAcknowledgment';
import PartsProcurementWO from './maintenance/PartsProcurementWO';
import MaintenanceReleaseCertificate from './maintenance/MaintenanceReleaseCertificate';
import WorkOrderAmOverrideDialog from './maintenance/WorkOrderAmOverrideDialog';
import WorkOrderCancelDialog from './maintenance/WorkOrderCancelDialog';
import CreateStandaloneIntimationDialog from './maintenance/CreateStandaloneIntimationDialog';
import LinkIntimationSourceDialog from './maintenance/LinkIntimationSourceDialog';
import StandaloneIntimationResponseDialog from './maintenance/StandaloneIntimationResponseDialog';
import PlanningReplyToClarificationDialog from './maintenance/PlanningReplyToClarificationDialog';
import IntimationDetailDialog from './maintenance/IntimationDetailDialog';
import EditIntimationDialog from './maintenance/EditIntimationDialog';
import ExtendDueDialog from './maintenance/ExtendDueDialog';

// Extracted components
import MaintenanceFilters from './maintenance/components/MaintenanceFilters';
import MaintenanceTable from './maintenance/components/MaintenanceTable';
import UpcomingMaintenanceTab from './maintenance/components/UpcomingMaintenanceTab';
import IntimationsTab from './maintenance/components/IntimationsTab';
import EdrInboxTab from './maintenance/components/EdrInboxTab';
import WorkOrderHistoryTab from './maintenance/components/WorkOrderHistoryTab';
import MaintenanceDashboard from './maintenance/components/MaintenanceDashboard';
import MaintenanceFormDialog from './maintenance/components/MaintenanceFormDialog';
import MaintenanceDetailDialog from './maintenance/components/MaintenanceDetailDialog';

// Hooks
import { useMaintenanceData } from './maintenance/hooks/useMaintenanceData';
import { useMaintenancePermissions } from './maintenance/hooks/useMaintenancePermissions';
import { useMaintenanceHandlers } from './maintenance/hooks/useMaintenanceHandlers';

// Utils
import {
  filterMaintenance,
  isOneTimeAmpSchedule,
  isAmpAssemblyNotApplicable
} from './maintenance/utils/maintenanceUtils';
import { getWorkOrderDisplayNumber } from '../utils/workOrderDisplayNumber';
import WorkflowGuide from './common/WorkflowGuide';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const TAB = {
  DASHBOARD: 'dashboard',
  ALL: 'all',
  STANDALONE: 'standalone',
  UPCOMING: 'upcoming',
  INTIMATIONS: 'intimations',
  EDR: 'edr',
  HISTORY: 'history'
};

const mapUpcomingTypeToIntimationType = (type) => {
  if (type === 'OVERHAUL') return 'MAINTENANCE';
  if (type === 'REPAIR') return 'REPAIR';
  if (type === 'INSPECTION' || type === 'MAINTENANCE' || type === 'SCHEDULED') return type;
  // TSN/TSI/TSP/SLL/OTL remain maintenance-class requests for intimation/WO creation.
  return 'MAINTENANCE';
};

const escapeCsvValue = (value) => {
  if (value === null || value === undefined) return '';
  const stringValue = String(value);
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

const toCsvContent = (rows) => {
  if (!rows || rows.length === 0) return '';
  const headers = Array.from(
    rows.reduce((set, row) => {
      Object.keys(row || {}).forEach((key) => set.add(key));
      return set;
    }, new Set())
  );
  const lines = rows.map((row) =>
    headers.map((header) => escapeCsvValue(row?.[header] ?? '')).join(',')
  );
  return [headers.join(','), ...lines].join('\n');
};

const downloadCsvFile = (filename, rows) => {
  const content = toCsvContent(rows);
  if (!content) return false;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
};

const Maintenance = () => {
  const [currentTab, setCurrentTab] = useState(TAB.DASHBOARD);
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/client/settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.data?.displayTimezone) setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';

  // Intimations (standalone "request to create WO") tab
  const [intimations, setIntimations] = useState([]);
  const [loadingIntimations, setLoadingIntimations] = useState(false);
  const [selectedIntimation, setSelectedIntimation] = useState(null);
  const [showArchivedOnly, setShowArchivedOnly] = useState(false);
  const [createStandaloneIntimationOpen, setCreateStandaloneIntimationOpen] = useState(false);
  const [standaloneIntimationRespondOpen, setStandaloneIntimationRespondOpen] = useState(false);
  const [planningReplyOpen, setPlanningReplyOpen] = useState(false);
  const [intimationDetailOpen, setIntimationDetailOpen] = useState(false);
  const [intimationEditOpen, setIntimationEditOpen] = useState(false);
  const [intimationPrefill, setIntimationPrefill] = useState(null);
  const [linkSourceOpen, setLinkSourceOpen] = useState(false);
  const [linkSourceMode, setLinkSourceMode] = useState('fromIntimation');
  const [linkSourceIntimation, setLinkSourceIntimation] = useState(null);
  const [linkSourceUpcomingItem, setLinkSourceUpcomingItem] = useState(null);
  const [extendDueOpen, setExtendDueOpen] = useState(false);
  const [extendDueItem, setExtendDueItem] = useState(null);
  
  // Upcoming maintenance states — no range filter by default (show all rows)
  const [upcomingRangeEnabled, setUpcomingRangeEnabled] = useState({
    days: false,
    hours: false,
    cycles: false,
    landings: false
  });
  const [upcomingDays, setUpcomingDays] = useState(90);
  const [customDays, setCustomDays] = useState('');
  const [upcomingHours, setUpcomingHours] = useState(100);
  const [customHours, setCustomHours] = useState('');
  const [upcomingCycles, setUpcomingCycles] = useState(500);
  const [customCycles, setCustomCycles] = useState('');
  const [upcomingLandings, setUpcomingLandings] = useState(250);
  const [customLandings, setCustomLandings] = useState('');
  const [upcomingAircraftFilter, setUpcomingAircraftFilter] = useState('ALL');
  const [upcomingTypeFilter, setUpcomingTypeFilter] = useState('ALL');
  const [upcomingScheduleFilter, setUpcomingScheduleFilter] = useState('ALL');
  const [upcomingSourceFilter, setUpcomingSourceFilter] = useState('ALL');
  const [upcomingAdsbBulletinTypeFilter, setUpcomingAdsbBulletinTypeFilter] = useState('ALL');
  const [upcomingExportRows, setUpcomingExportRows] = useState([]);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [aircraftFilter, setAircraftFilter] = useState('ALL');

  // Work order history tab filters
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [historyPriorityFilter, setHistoryPriorityFilter] = useState('ALL');
  const [historyTypeFilter, setHistoryTypeFilter] = useState('ALL');
  const [historyAircraftFilter, setHistoryAircraftFilter] = useState('ALL');
  
  const { user, viewGrants } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();

  const hasRoleMaintenanceAccess = roleMatches(user?.role, [
    ROLES.CA_MANAGER,
    ROLES.CA_TECHNICAL_OFFICER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.CAMO_QM_OFFICER,
    ROLES.PLANNING_MANAGER,
    ROLES.STORE_INSPECTOR,
    ROLES.MRO_MANAGER,
    ROLES.MRO_ENGINEER,
    ROLES.MRO_QM_MANAGER,
    ROLES.MRO_QM_OFFICER,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ]);
  const hasMaintenanceAccess = hasRoleMaintenanceAccess || hasGrantModuleAccess(viewGrants, 'maintenance');

  // Custom hooks
  const {
    maintenance,
    aircraft,
    completedWorkOrders,
    loadingCompleted,
    upcomingMaintenance,
    loadingUpcoming,
    refreshingUpcoming,
    fetchMaintenance,
    fetchCompletedWorkOrders,
    fetchUpcomingMaintenance,
    setMaintenance
  } = useMaintenanceData();

  const permissions = useMaintenancePermissions(user);

  const canViewMaintenanceTab = (tabKey) => {
    if (hasRoleMaintenanceAccess) {
      if (tabKey === TAB.STANDALONE) return permissions.canViewStandaloneEmergencyTab;
      if (tabKey === TAB.EDR) return permissions.canViewEdrInbox;
      return true;
    }
    return hasGrantTabAccess(viewGrants, 'maintenance', tabKey);
  };

  useEffect(() => {
    if (!canViewMaintenanceTab(TAB.STANDALONE) && currentTab === TAB.STANDALONE) {
      setCurrentTab(TAB.ALL);
    }
  }, [currentTab, hasRoleMaintenanceAccess, viewGrants, permissions.canViewStandaloneEmergencyTab]);

  useEffect(() => {
    if (!canViewMaintenanceTab(TAB.EDR) && currentTab === TAB.EDR) {
      setCurrentTab(TAB.DASHBOARD);
    }
  }, [currentTab, hasRoleMaintenanceAccess, viewGrants, permissions.canViewEdrInbox]);

  const {
    open,
    setOpen,
    detailOpen,
    setDetailOpen,
    detailTab,
    setDetailTab,
    selectedMaintenance,
    setSelectedMaintenance,
    formData,
    setFormData,
    camoQmAckOpen,
    setCamoQmAckOpen,
    planningOpen,
    setPlanningOpen,
    storeOpen,
    setStoreOpen,
    externalAgencyOpen,
    setExternalAgencyOpen,
    mroOpen,
    setMroOpen,
    mroEngineerOpen,
    setMroEngineerOpen,
    mroQmCompletionOpen,
    setMroQmCompletionOpen,
    camoQmCompletionOpen,
    setCamoQmCompletionOpen,
    partsProcurementOpen,
    setPartsProcurementOpen,
    releaseCertificateOpen,
    setReleaseCertificateOpen,
    cancelOpen,
    setCancelOpen,
    handleSubmit,
    handleClose,
    handleEdit,
    handleCancel,
    handleWorkflowAction,
    handleWorkflowSuccess,
    handleViewDetails,
    handleRefreshWorkOrder
  } = useMaintenanceHandlers(fetchMaintenance, displayTimezone);

  const [amOverrideOpen, setAmOverrideOpen] = useState(false);
  const [pendingEditItem, setPendingEditItem] = useState(null);
  const [mroHandoverOpen, setMroHandoverOpen] = useState(false);
  const [mroAssignEngineersOpen, setMroAssignEngineersOpen] = useState(false);

  const handleEditRequest = (item) => {
    if (permissions.requiresAmOverrideForWorkOrderChange) {
      setPendingEditItem(item);
      setAmOverrideOpen(true);
      return;
    }
    handleEdit(item);
  };

  const handleConfirmAmOverrideEdit = (justification) => {
    if (pendingEditItem) {
      handleEdit(pendingEditItem, justification);
    }
    setPendingEditItem(null);
    setAmOverrideOpen(false);
  };

  const fetchIntimations = async (archivedOnly) => {
    const only = archivedOnly !== undefined ? archivedOnly : showArchivedOnly;
    setLoadingIntimations(true);
    try {
      const url = only ? '/api/intimations?showArchivedOnly=true' : '/api/intimations';
      const res = await axios.get(url);
      setIntimations(res.data || []);
    } catch (err) {
      console.error('Failed to fetch intimations:', err);
      setIntimations([]);
    } finally {
      setLoadingIntimations(false);
    }
  };

  const fetchUpcomingWithFilters = useCallback(() => {
    fetchUpcomingMaintenance(
      upcomingDays,
      upcomingAircraftFilter,
      upcomingTypeFilter,
      upcomingRangeEnabled,
      upcomingHours,
      upcomingCycles,
      upcomingLandings
    );
  }, [
    fetchUpcomingMaintenance,
    upcomingAircraftFilter,
    upcomingTypeFilter,
    upcomingRangeEnabled,
    upcomingDays,
    upcomingHours,
    upcomingCycles,
    upcomingLandings
  ]);

  const handleLinkSourceSuccess = () => {
    fetchIntimations(showArchivedOnly);
    fetchUpcomingWithFilters();
  };

  const openLinkFromIntimation = (intimation) => {
    setLinkSourceMode('fromIntimation');
    setLinkSourceIntimation(intimation);
    setLinkSourceUpcomingItem(null);
    setLinkSourceOpen(true);
  };

  const openLinkFromUpcoming = (item) => {
    fetchIntimations(false);
    setLinkSourceMode('fromUpcoming');
    setLinkSourceUpcomingItem(item);
    setLinkSourceIntimation(null);
    setLinkSourceOpen(true);
  };

  const upcomingTabInitialLoadRef = useRef(false);

  const canFilterUpcoming =
    !(
      (upcomingRangeEnabled.days && upcomingDays === 'custom') ||
      (upcomingRangeEnabled.hours && upcomingHours === 'custom') ||
      (upcomingRangeEnabled.cycles && upcomingCycles === 'custom') ||
      (upcomingRangeEnabled.landings && upcomingLandings === 'custom')
    );

  // Initial load once when opening the Upcoming tab (no auto-refresh on filter changes)
  useEffect(() => {
    if (currentTab !== TAB.UPCOMING) return;
    if (upcomingTabInitialLoadRef.current) return;
    if (!canFilterUpcoming) return;
    upcomingTabInitialLoadRef.current = true;
    fetchUpcomingWithFilters();
  }, [currentTab, canFilterUpcoming, fetchUpcomingWithFilters]);

  // Fetch intimations when "Intimations to Create WO" tab is selected or showArchivedOnly toggles
  useEffect(() => {
    if (currentTab === TAB.INTIMATIONS) {
      fetchIntimations(showArchivedOnly);
      if (canFilterUpcoming) {
        fetchUpcomingWithFilters();
      }
    }
  }, [currentTab, showArchivedOnly]);

  // Fetch completed work orders when Work Order History tab is selected
  useEffect(() => {
    if (currentTab === TAB.HISTORY) {
      fetchCompletedWorkOrders();
    }
  }, [currentTab, fetchCompletedWorkOrders]);

  // Handle navigation state for opening Intimations tab and selecting an intimation (e.g. from Upcoming "View intimation")
  const openIntimationById = useCallback(async (intimationId) => {
    const id = intimationId != null ? String(intimationId) : null;
    if (!id) return;
    setShowArchivedOnly(false);
    setCurrentTab(TAB.INTIMATIONS);
    try {
      const res = await axios.get(`/api/intimations/${id}`);
      setSelectedIntimation(res.data);
      setIntimationDetailOpen(true);
    } catch (err) {
      console.error('Failed to open intimation:', err);
      showToast(err.response?.data?.message || 'Intimation not found', 'error');
    }
    fetchIntimations(false);
  }, [showToast]);

  useEffect(() => {
    if (location.state?.openIntimationsTab && location.state?.intimationId != null) {
      const id = String(location.state.intimationId);
      openIntimationById(id);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (location.state?.openIntimationsTab) {
      setCurrentTab(TAB.INTIMATIONS);
      fetchIntimations(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state?.openIntimationsTab, location.state?.intimationId, openIntimationById]);

  useEffect(() => {
    if (location.state?.openEdrTab && canViewMaintenanceTab(TAB.EDR)) {
      setCurrentTab(TAB.EDR);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.state?.openEdrTab, permissions.canViewEdrInbox]);
  // Handle navigation state for selecting a work order
  useEffect(() => {
    if (location.state?.selectedWorkOrderId) {
      const workOrderId = location.state.selectedWorkOrderId;
      // Find the work order and open its details
      const findAndOpenWorkOrder = async () => {
        try {
          const response = await axios.get(`/api/maintenance/${workOrderId}`);
          setSelectedMaintenance(response.data);
          setDetailOpen(true);
          // Clear the navigation state
          window.history.replaceState({}, document.title);
        } catch (error) {
          console.error('Error fetching work order from navigation state:', error);
          showToast('Work order not found', 'error');
        }
      };
      
      // Wait for maintenance list to load, then open the work order
      if (maintenance.length > 0) {
        findAndOpenWorkOrder();
      } else {
        // If maintenance not loaded yet, wait a bit and try again
        const timer = setTimeout(() => {
          findAndOpenWorkOrder();
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectedWorkOrderId, maintenance.length]);

  // Filter maintenance based on search and filters
  const filteredMaintenance = filterMaintenance(
    maintenance,
    searchTerm,
    statusFilter,
    priorityFilter,
    typeFilter,
    aircraftFilter
  );

  const standaloneMaintenance = maintenance.filter((m) => m.standaloneEmergencyRepair);
  const filteredStandaloneMaintenance = filterMaintenance(
    standaloneMaintenance,
    searchTerm,
    statusFilter,
    priorityFilter,
    typeFilter,
    aircraftFilter
  );

  const openEmergencyStandaloneForm = () => {
    setFormData({
      aircraftId: '',
      type: 'REPAIR',
      title: '',
      description: '',
      priority: 'CRITICAL',
      status: 'SCHEDULED',
      scheduledDate: null,
      scheduleMode: 'FULL_DAY',
      scheduledEndDate: null,
      inspectionType: '',
      ampScheduleId: '',
      ampChecks: [],
      creationSourceRemarks: 'Emergency defect — stand-alone work order. CAMO QM review required.',
      linkedIntimationIds: [],
      workOrderCsvFile: null,
      standaloneEmergencyRepair: true
    });
    setOpen(true);
  };

  const handleDownloadCurrentTabCsv = () => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');

    if (currentTab === TAB.ALL) {
      const rows = filteredMaintenance.map((item) => ({
        workOrderNumber: getWorkOrderDisplayNumber(item),
        aircraft: item.aircraftId?.registration || '',
        type: item.type || '',
        title: item.title || '',
        priority: item.priority || '',
        status: item.status || '',
        scheduledDate: item.scheduledDate ? new Date(item.scheduledDate).toISOString() : ''
      }));
      const ok = downloadCsvFile(`maintenance-all-work-orders-${timestamp}.csv`, rows);
      if (!ok) showToast('No rows available to export for this tab.', 'info');
      return;
    }

    if (currentTab === TAB.STANDALONE) {
      const rows = filteredStandaloneMaintenance.map((item) => ({
        workOrderNumber: getWorkOrderDisplayNumber(item),
        aircraft: item.aircraftId?.registration || '',
        type: item.type || '',
        title: item.title || '',
        priority: item.priority || '',
        status: item.status || '',
        scheduledDate: item.scheduledDate ? new Date(item.scheduledDate).toISOString() : ''
      }));
      const ok = downloadCsvFile(`maintenance-standalone-work-orders-${timestamp}.csv`, rows);
      if (!ok) showToast('No rows available to export for this tab.', 'info');
      return;
    }

    if (currentTab === TAB.UPCOMING) {
      const rows = (upcomingExportRows || []).map((item) => ({
        source: item.source || '',
        type: item.type || '',
        title: item.title || '',
        description: item.description || '',
        aircraft: item.aircraftRegistration || '',
        componentName: item.componentName || item.componentDescription || '',
        componentCode: item.componentCode || '',
        assemblyType: item.assemblyType || '',
        assemblyNotApplicable:
          item.source === 'AMP_SCHEDULE' ? (isAmpAssemblyNotApplicable(item) ? 'Yes' : 'No') : '',
        recurrence:
          item.source === 'AMP_SCHEDULE'
            ? (isOneTimeAmpSchedule(item) ? 'One time' : item.recurrenceType || '')
            : '',
        dueDate: item.dueDate ? new Date(item.dueDate).toISOString() : '',
        remainingDays: item.remainingByUnit?.days ?? '',
        remainingHours: item.remainingByUnit?.hours ?? '',
        remainingCycles: item.remainingByUnit?.cycles ?? '',
        remainingLandings: item.remainingByUnit?.landings ?? '',
        priority: item.priority || '',
        status: item.status || ''
      }));
      const ok = downloadCsvFile(`maintenance-upcoming-${timestamp}.csv`, rows);
      if (!ok) showToast('No rows available to export for this tab.', 'info');
      return;
    }

    if (currentTab === TAB.INTIMATIONS) {
      const rows = (intimations || []).map((row) => ({
        title: row.title || '',
        source: row.sourceType
          ? `${row.sourceType}${row.sourcePlainText ? ` — ${row.sourcePlainText}` : ''}`
          : (row.sourcePlainText || ''),
        aircraft: row.aircraftId?.registration || '',
        createdBy: row.createdBy ? `${row.createdBy.firstName || ''} ${row.createdBy.lastName || ''}`.trim() : '',
        createdAt: row.createdAt ? new Date(row.createdAt).toISOString() : '',
        status: row.status || '',
        workOrderNumber: row.workOrderNumber || row.linkedWorkOrderId?.workOrderNumber || ''
      }));
      const ok = downloadCsvFile(`maintenance-intimations-${timestamp}.csv`, rows);
      if (!ok) showToast('No rows available to export for this tab.', 'info');
      return;
    }

    if (currentTab === TAB.EDR) {
      (async () => {
        try {
          const res = await axios.get('/api/maintenance/edr/inbox');
          const list = Array.isArray(res.data) ? res.data : [];
          const rows = list.map((row) => ({
            sentAt: row.sentAt ? new Date(row.sentAt).toISOString() : '',
            edrReferenceNumber: row.edrReferenceNumber || row.form?.edrReferenceNumber || '',
            workOrderNumber: row.workOrderNumber || '',
            title: row.title || '',
            aircraft: row.aircraftRegistration || '',
            authorisationNumber: row.icplNumber || '',
            sentBy: row.sentBy ? `${row.sentBy.firstName || ''} ${row.sentBy.lastName || ''}`.trim() : '',
            status: row.status || ''
          }));
          const ok = downloadCsvFile(`maintenance-edr-inbox-${timestamp}.csv`, rows);
          if (!ok) showToast('No rows available to export for this tab.', 'info');
        } catch (e) {
          showToast(e.response?.data?.message || 'Failed to export EDR inbox', 'error');
        }
      })();
      return;
    }

    if (currentTab === TAB.HISTORY) {
      const rows = (completedWorkOrders || []).map((item) => ({
        workOrderNumber: getWorkOrderDisplayNumber(item),
        aircraft: item.aircraftId?.registration || '',
        type: item.type || '',
        title: item.title || '',
        priority: item.priority || '',
        scheduledDate: item.scheduledDate ? new Date(item.scheduledDate).toISOString() : '',
        completedDate: item.completedDate ? new Date(item.completedDate).toISOString() : '',
        mroEngineer: item.mroEngineerCompletedBy
          ? `${item.mroEngineerCompletedBy.firstName || ''} ${item.mroEngineerCompletedBy.lastName || ''}`.trim()
          : '',
        camoQmAck: item.camoQmCompletionAcknowledged ? 'Yes' : 'No',
        releaseCertificate: item.maintenanceRelease?.releasedAt ? 'Yes' : 'No'
      }));
      const ok = downloadCsvFile(`maintenance-work-order-history-${timestamp}.csv`, rows);
      if (!ok) showToast('No rows available to export for this tab.', 'info');
    }
  };

  if (!hasMaintenanceAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          You do not have permission to access Maintenance & Inspections.
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Maintenance & Inspections</Typography>
          {currentTab !== TAB.DASHBOARD && (
            <>
              {currentTab === TAB.STANDALONE && permissions.canStandAloneEmergencyWorkOrder && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                  <Button variant="contained" color="warning" startIcon={<Add />} onClick={openEmergencyStandaloneForm}>
                    Create emergency stand-alone WO
                  </Button>
                  <Typography variant="caption" color="warning.main">
                    Standalone WO is an Emergency WO, future charters will be cancelled, Please inform SALES AND OPERATIONS team
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {currentTab !== TAB.DASHBOARD && (
                  <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadCurrentTabCsv}>
                    Download CSV
                  </Button>
                )}
                {currentTab !== TAB.STANDALONE &&
                  currentTab !== TAB.EDR &&
                  currentTab !== TAB.HISTORY &&
                  permissions.canManageMaintenance && (
                  <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
                    Schedule Maintenance
                  </Button>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            {canViewMaintenanceTab(TAB.DASHBOARD) && (
              <Tab icon={<Dashboard />} iconPosition="start" label="Dashboard" value={TAB.DASHBOARD} />
            )}
            {canViewMaintenanceTab(TAB.ALL) && <Tab label="All Work Orders" value={TAB.ALL} />}
            {canViewMaintenanceTab(TAB.STANDALONE) && (
              <Tab label="StandAlone WO" value={TAB.STANDALONE} />
            )}
            {canViewMaintenanceTab(TAB.UPCOMING) && <Tab label="Upcoming Maintenance" value={TAB.UPCOMING} />}
            {canViewMaintenanceTab(TAB.INTIMATIONS) && <Tab label="Intimations to Create WO" value={TAB.INTIMATIONS} />}
            {canViewMaintenanceTab(TAB.HISTORY) && <Tab label="Work Order History" value={TAB.HISTORY} />}
            {canViewMaintenanceTab(TAB.EDR) && <Tab label="EDR" value={TAB.EDR} />}
          </Tabs>
        </Paper>

        <WorkflowGuide key={currentTab} variant="maintenance" tab={currentTab} />

        {/* Dashboard Tab Content */}
        {currentTab === TAB.DASHBOARD && (
          <MaintenanceDashboard />
        )}

        {/* All Work Orders Tab Content */}
        {currentTab === TAB.ALL && (
          <>
            <MaintenanceFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              priorityFilter={priorityFilter}
              setPriorityFilter={setPriorityFilter}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              aircraftFilter={aircraftFilter}
              setAircraftFilter={setAircraftFilter}
              aircraft={aircraft}
              maintenance={maintenance}
              filteredMaintenance={filteredMaintenance}
            />

            <MaintenanceTable
              filteredMaintenance={filteredMaintenance}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              priorityFilter={priorityFilter}
              typeFilter={typeFilter}
              canManageMaintenance={permissions.canManageMaintenance}
              canEditWorkOrder={permissions.canEditWorkOrder}
              canCancelWorkOrder={permissions.canCancelWorkOrder}
              canAcknowledge={permissions.canAcknowledge}
              canPlan={permissions.canPlan}
              canStoreAction={permissions.canStoreAction}
              canExternalAgencyAction={permissions.canExternalAgencyAction}
              canMROAction={permissions.canMROAction}
              canMROEngineerComplete={permissions.canMROEngineerComplete}
              canMROQMCompletionAck={permissions.canMROQMCompletionAck}
              canCAMOQMCompletionAck={permissions.canCAMOQMCompletionAck}
              handleViewDetails={handleViewDetails}
              handleEdit={handleEditRequest}
              handleCancel={handleCancel}
              handleWorkflowAction={handleWorkflowAction}
            />
          </>
        )}

        {/* StandAlone WO (emergency) — CAMO QM review required like a normal work order */}
        {currentTab === TAB.STANDALONE && canViewMaintenanceTab(TAB.STANDALONE) && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These work orders are created in <strong>SCHEDULED</strong> status. CAMO QM must review and acknowledge
              creation before Planning can act, the same as a normal work order. Inform Sales and Operations — future
              charters on this aircraft may be cancelled.
            </Typography>
            <MaintenanceFilters
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              priorityFilter={priorityFilter}
              setPriorityFilter={setPriorityFilter}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              aircraftFilter={aircraftFilter}
              setAircraftFilter={setAircraftFilter}
              aircraft={aircraft}
              maintenance={standaloneMaintenance}
              filteredMaintenance={filteredStandaloneMaintenance}
            />
            <MaintenanceTable
              filteredMaintenance={filteredStandaloneMaintenance}
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              priorityFilter={priorityFilter}
              typeFilter={typeFilter}
              canManageMaintenance={permissions.canManageMaintenance}
              canEditWorkOrder={permissions.canEditWorkOrder}
              canCancelWorkOrder={permissions.canCancelWorkOrder}
              canAcknowledge={permissions.canAcknowledge}
              canPlan={permissions.canPlan}
              canStoreAction={permissions.canStoreAction}
              canExternalAgencyAction={permissions.canExternalAgencyAction}
              canMROAction={permissions.canMROAction}
              canMROEngineerComplete={permissions.canMROEngineerComplete}
              canMROQMCompletionAck={permissions.canMROQMCompletionAck}
              canCAMOQMCompletionAck={permissions.canCAMOQMCompletionAck}
              handleViewDetails={handleViewDetails}
              handleEdit={handleEditRequest}
              handleCancel={handleCancel}
              handleWorkflowAction={handleWorkflowAction}
            />
          </>
        )}

        {/* Upcoming Maintenance Tab Content */}
        {currentTab === TAB.UPCOMING && (
          <UpcomingMaintenanceTab
            upcomingMaintenance={upcomingMaintenance}
            displayTimezone={displayTimezone}
            loadingUpcoming={loadingUpcoming}
            refreshingUpcoming={refreshingUpcoming}
            upcomingDays={upcomingDays}
            setUpcomingDays={setUpcomingDays}
            customDays={customDays}
            setCustomDays={setCustomDays}
            upcomingHours={upcomingHours}
            setUpcomingHours={setUpcomingHours}
            customHours={customHours}
            setCustomHours={setCustomHours}
            upcomingCycles={upcomingCycles}
            setUpcomingCycles={setUpcomingCycles}
            customCycles={customCycles}
            setCustomCycles={setCustomCycles}
            upcomingLandings={upcomingLandings}
            setUpcomingLandings={setUpcomingLandings}
            customLandings={customLandings}
            setCustomLandings={setCustomLandings}
            upcomingRangeEnabled={upcomingRangeEnabled}
            setUpcomingRangeEnabled={setUpcomingRangeEnabled}
            upcomingAircraftFilter={upcomingAircraftFilter}
            setUpcomingAircraftFilter={setUpcomingAircraftFilter}
            upcomingTypeFilter={upcomingTypeFilter}
            setUpcomingTypeFilter={setUpcomingTypeFilter}
            upcomingScheduleFilter={upcomingScheduleFilter}
            setUpcomingScheduleFilter={setUpcomingScheduleFilter}
            upcomingSourceFilter={upcomingSourceFilter}
            setUpcomingSourceFilter={setUpcomingSourceFilter}
            upcomingAdsbBulletinTypeFilter={upcomingAdsbBulletinTypeFilter}
            setUpcomingAdsbBulletinTypeFilter={setUpcomingAdsbBulletinTypeFilter}
            aircraft={aircraft}
            canManageMaintenance={permissions.canManageMaintenance}
            canCreateIntimation={permissions.canCreateIntimation}
            canLinkIntimationSource={permissions.canLinkIntimationSource()}
            setSelectedMaintenance={setSelectedMaintenance}
            setDetailOpen={setDetailOpen}
            setDetailTab={setDetailTab}
            setFormData={setFormData}
            setOpen={setOpen}
            onCreateIntimation={(item) => {
              setIntimationPrefill({
                aircraftId: item.aircraftId?.toString?.() || item.aircraftId,
                title: item.title || '',
                type: mapUpcomingTypeToIntimationType(item.type),
                description: item.description || '',
                scheduledDate: item.dueDate || null,
                sourceType: item.source || undefined,
                sourceId: item.sourceId?.toString?.() || item.sourceId,
                sourceMetadata: item.tsrIndex != null ? { tsrIndex: item.tsrIndex } : undefined,
                upcomingItem: item
              });
              setCreateStandaloneIntimationOpen(true);
            }}
            onLinkExistingIntimation={openLinkFromUpcoming}
            onViewIntimation={openIntimationById}
            canRequestDueExtension={permissions.canRequestDueExtension}
            canApproveDueExtension={permissions.canApproveDueExtension}
            onExtendDue={(item) => {
              setExtendDueItem(item);
              setExtendDueOpen(true);
            }}
            onFilteredUpcomingChange={setUpcomingExportRows}
            onFilter={fetchUpcomingWithFilters}
            canFilter={canFilterUpcoming}
            showToast={showToast}
          />
        )}

        {/* Intimations to Create WO Tab Content */}
        {currentTab === TAB.INTIMATIONS && (
          <IntimationsTab
            intimations={intimations}
            displayTimezone={displayTimezone}
            loadingIntimations={loadingIntimations}
            showArchivedOnly={showArchivedOnly}
            onShowArchivedChange={(value) => {
              setShowArchivedOnly(value);
              fetchIntimations(value);
            }}
            fetchIntimations={() => fetchIntimations(showArchivedOnly)}
            canCreateIntimation={permissions.canCreateIntimation({})}
            canLinkIntimationSource={permissions.canLinkIntimationSource()}
            canRespondToIntimation={permissions.canRespondToIntimation()}
            canReplyToClarification={permissions.canCreateIntimation({})}
            onOpenCreateDialog={() => setCreateStandaloneIntimationOpen(true)}
            onLinkToUpcoming={openLinkFromIntimation}
            onOpenRespondDialog={() => setStandaloneIntimationRespondOpen(true)}
            onOpenPlanningReply={() => setPlanningReplyOpen(true)}
            setSelectedIntimation={setSelectedIntimation}
            onOpenView={(intimation) => {
              setSelectedIntimation(intimation);
              setIntimationDetailOpen(true);
            }}
            onOpenEdit={(intimation) => {
              setSelectedIntimation(intimation);
              setIntimationEditOpen(true);
            }}
            onNavigateToWorkOrder={(workOrderId) => {
              setCurrentTab(TAB.ALL);
              setDetailOpen(true);
              axios.get(`/api/maintenance/${workOrderId}`).then((r) => setSelectedMaintenance(r.data)).catch(() => showToast('Work order not found', 'error'));
            }}
            aircraft={aircraft}
            upcomingMaintenance={upcomingMaintenance}
            showToast={showToast}
          />
        )}

        {currentTab === TAB.EDR && canViewMaintenanceTab(TAB.EDR) && (
          <EdrInboxTab
            onOpenWorkOrder={handleViewDetails}
            aircraft={aircraft}
            displayTimezone={displayTimezone}
            onWorkOrderCreated={(wo) => {
              fetchMaintenance();
              if (wo?._id) {
                handleViewDetails(wo);
              }
            }}
          />
        )}

        {currentTab === TAB.HISTORY && (
          <WorkOrderHistoryTab
            completedWorkOrders={completedWorkOrders}
            loadingCompleted={loadingCompleted}
            aircraft={aircraft}
            displayTimezone={displayTimezone}
            searchTerm={historySearchTerm}
            setSearchTerm={setHistorySearchTerm}
            priorityFilter={historyPriorityFilter}
            setPriorityFilter={setHistoryPriorityFilter}
            typeFilter={historyTypeFilter}
            setTypeFilter={setHistoryTypeFilter}
            aircraftFilter={historyAircraftFilter}
            setAircraftFilter={setHistoryAircraftFilter}
            onViewDetails={handleViewDetails}
          />
        )}

        {/* Create/Edit Maintenance Dialog */}
        <MaintenanceFormDialog
          open={open}
          onClose={handleClose}
          formData={formData}
          setFormData={setFormData}
          aircraft={aircraft}
          selectedMaintenance={selectedMaintenance}
          onSubmit={handleSubmit}
          displayTimezone={displayTimezone}
          standaloneEmergencyMode={Boolean(!selectedMaintenance && formData.standaloneEmergencyRepair)}
          scheduledDateDisabled={
            Boolean(selectedMaintenance) && !permissions.canEditWorkOrderScheduledDate(selectedMaintenance)
          }
        />

        {/* Workflow Action Dialogs */}
        <CAMOQMAcknowledgment
          open={camoQmAckOpen}
          onClose={() => setCamoQmAckOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <PlanningActions
          open={planningOpen}
          onClose={() => setPlanningOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
          displayTimezone={displayTimezone}
        />
        <StoreActions
          open={storeOpen}
          onClose={() => setStoreOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <ExternalAgencyActions
          open={externalAgencyOpen}
          onClose={() => setExternalAgencyOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <MROActions
          open={mroOpen}
          onClose={() => setMroOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <MROEngineerCompletion
          open={mroEngineerOpen}
          onClose={() => setMroEngineerOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <MROHandoverDialog
          open={mroHandoverOpen}
          onClose={() => setMroHandoverOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={() => {
            setMroHandoverOpen(false);
            handleWorkflowSuccess();
          }}
        />
        <MROAssignEngineersDialog
          open={mroAssignEngineersOpen}
          onClose={() => setMroAssignEngineersOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={() => {
            setMroAssignEngineersOpen(false);
            handleWorkflowSuccess();
          }}
        />
        <MroQmCompletionAcknowledgment
          open={mroQmCompletionOpen}
          onClose={() => setMroQmCompletionOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <CAMOQMCompletionAcknowledgment
          open={camoQmCompletionOpen}
          onClose={() => setCamoQmCompletionOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <PartsProcurementWO
          open={partsProcurementOpen}
          onClose={() => setPartsProcurementOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <MaintenanceReleaseCertificate
          open={releaseCertificateOpen}
          onClose={() => setReleaseCertificateOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
        />
        <CreateStandaloneIntimationDialog
          open={createStandaloneIntimationOpen}
          onClose={() => { setCreateStandaloneIntimationOpen(false); setIntimationPrefill(null); }}
          aircraft={aircraft}
          initialPrefill={intimationPrefill}
          onSuccess={() => {
            fetchIntimations(showArchivedOnly);
            fetchMaintenance();
            fetchUpcomingWithFilters();
          }}
          displayTimezone={displayTimezone}
        />
        <LinkIntimationSourceDialog
          open={linkSourceOpen}
          onClose={() => {
            setLinkSourceOpen(false);
            setLinkSourceIntimation(null);
            setLinkSourceUpcomingItem(null);
          }}
          mode={linkSourceMode}
          intimation={linkSourceIntimation}
          upcomingItem={linkSourceUpcomingItem}
          intimations={intimations}
          upcomingMaintenance={upcomingMaintenance}
          displayTimezone={displayTimezone}
          onSuccess={handleLinkSourceSuccess}
        />
        <StandaloneIntimationResponseDialog
          open={standaloneIntimationRespondOpen}
          onClose={() => { setStandaloneIntimationRespondOpen(false); setSelectedIntimation(null); }}
          intimation={selectedIntimation}
          upcomingMaintenance={upcomingMaintenance}
          displayTimezone={displayTimezone}
          onSuccess={() => { fetchIntimations(showArchivedOnly); fetchMaintenance(); }}
        />
        <PlanningReplyToClarificationDialog
          open={planningReplyOpen}
          onClose={() => { setPlanningReplyOpen(false); setSelectedIntimation(null); }}
          intimation={selectedIntimation}
          onSuccess={() => {
            fetchIntimations(showArchivedOnly);
            fetchMaintenance();
            fetchUpcomingWithFilters();
          }}
        />
        <IntimationDetailDialog
          open={intimationDetailOpen}
          onClose={() => { setIntimationDetailOpen(false); setSelectedIntimation(null); }}
          intimation={selectedIntimation}
          displayTimezone={displayTimezone}
          upcomingMaintenance={upcomingMaintenance}
          onNavigateToWorkOrder={(workOrderId) => {
            setIntimationDetailOpen(false);
            setCurrentTab(TAB.ALL);
            setDetailOpen(true);
            axios.get(`/api/maintenance/${workOrderId}`).then((r) => setSelectedMaintenance(r.data)).catch(() => showToast('Work order not found', 'error'));
          }}
        />
        <EditIntimationDialog
          open={intimationEditOpen}
          onClose={() => { setIntimationEditOpen(false); setSelectedIntimation(null); }}
          intimation={selectedIntimation}
          aircraft={aircraft}
          onSuccess={() => { fetchIntimations(showArchivedOnly); setIntimationEditOpen(false); setSelectedIntimation(null); }}
          displayTimezone={displayTimezone}
        />
        <ExtendDueDialog
          open={extendDueOpen}
          onClose={() => { setExtendDueOpen(false); setExtendDueItem(null); }}
          item={extendDueItem}
          canRequestDueExtension={extendDueItem ? permissions.canRequestDueExtension(extendDueItem) : false}
          canApproveDueExtension={permissions.canApproveDueExtension}
          canRevokeDueExtension={permissions.canRevokeDueExtension}
          displayTimezone={displayTimezone}
          onSuccess={fetchUpcomingWithFilters}
        />

        {/* Work Order Detail Dialog */}
        <MaintenanceDetailDialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          selectedMaintenance={selectedMaintenance}
          detailTab={detailTab}
          setDetailTab={setDetailTab}
          user={user}
          displayTimezone={displayTimezone}
          canAcknowledge={permissions.canAcknowledge}
          canViewCamoQmReviewTab={permissions.canViewCamoQmReviewTab}
          canPlan={permissions.canPlan}
          canProvideProcurementRequirements={permissions.canProvideProcurementRequirements}
          canStoreAction={permissions.canStoreAction}
          canCreatePartsProcurement={permissions.canCreatePartsProcurement}
          canExternalAgencyAction={permissions.canExternalAgencyAction}
          canMROAction={permissions.canMROAction}
          canMROAssignEngineers={permissions.canMROAssignEngineers}
          canMROHandover={permissions.canMROHandover}
          canMROEngineerComplete={permissions.canMROEngineerComplete}
          canMROQMCompletionAck={permissions.canMROQMCompletionAck}
          canCAMOQMCompletionAck={permissions.canCAMOQMCompletionAck}
          canComplete={permissions.canComplete}
          canResumeFromHold={permissions.canResumeFromHold}
          canIssueReleaseCertificate={permissions.canIssueReleaseCertificate}
          canEditWorkOrder={permissions.canEditWorkOrder}
          canCancelWorkOrder={permissions.canCancelWorkOrder}
          handleWorkflowAction={handleWorkflowAction}
          handleWorkflowSuccess={handleWorkflowSuccess}
          handleEdit={handleEditRequest}
          handleCancel={handleCancel}
          setDetailOpen={setDetailOpen}
          setPlanningOpen={setPlanningOpen}
          onOpenMroHandover={() => setMroHandoverOpen(true)}
          onOpenMroAssignEngineers={() => setMroAssignEngineersOpen(true)}
          onRefresh={handleRefreshWorkOrder}
        />
        <WorkOrderAmOverrideDialog
          open={amOverrideOpen}
          onClose={() => {
            setAmOverrideOpen(false);
            setPendingEditItem(null);
          }}
          onConfirm={handleConfirmAmOverrideEdit}
          actionLabel="edit this work order"
        />
        <WorkOrderCancelDialog
          open={cancelOpen}
          onClose={() => setCancelOpen(false)}
          workOrder={selectedMaintenance}
          onSuccess={handleWorkflowSuccess}
          requiresAmOverride={permissions.requiresAmOverrideForWorkOrderChange}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default Maintenance;
